/**
 * Bookd - Insurance Recommendation Component
 * Displays insurance partner options at checkout with disclaimer
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { 
  Shield, ExternalLink, Star, CheckCircle, 
  AlertCircle, Info, ChevronDown, ChevronUp 
} from 'lucide-react';

// Insurance partners data
const INSURANCE_PARTNERS = [
  {
    id: 'flip',
    name: 'Flip Insurance',
    description: 'Best for freelancers & gig workers',
    tagline: 'Liability coverage designed for the modern workforce',
    url: 'https://getflip.com',
    features: ['Liability Coverage', 'Equipment Protection', 'Event Coverage'],
    recommended: true,
    color: 'bg-emerald-500'
  },
  {
    id: 'thimble',
    name: 'Thimble',
    description: 'On-demand liability insurance',
    tagline: 'Insurance by the hour, day, or month',
    url: 'https://thimble.com',
    features: ['Flexible Coverage', 'Instant Certificates', 'Pay-Per-Job'],
    recommended: false,
    color: 'bg-blue-500'
  }
];

const InsuranceRecommendation = ({ 
  onDecline, 
  showDeclineCheckbox = true,
  context = 'checkout' // 'checkout' | 'settings' | 'signup'
}) => {
  const [hasDeclined, setHasDeclined] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const handlePartnerClick = (partner) => {
    // Open partner website in new tab
    window.open(partner.url, '_blank', 'noopener,noreferrer');
  };

  const handleDecline = (checked) => {
    setHasDeclined(checked);
    if (onDecline) {
      onDecline(checked);
    }
  };

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-primary/10">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Protect Your Gig</CardTitle>
              <CardDescription>
                Get coverage from our recommended partners
              </CardDescription>
            </div>
          </div>
          {context === 'checkout' && (
            <Badge variant="outline" className="text-xs">
              Optional
            </Badge>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Insurance Partner Cards */}
        <div className="grid gap-3">
          {INSURANCE_PARTNERS.map((partner) => (
            <div 
              key={partner.id}
              className={`relative p-4 rounded-xl border transition-all hover:border-primary/50 hover:shadow-md cursor-pointer ${
                partner.recommended ? 'border-primary/30 bg-primary/5' : 'border-border'
              }`}
              onClick={() => handlePartnerClick(partner)}
              data-testid={`insurance-partner-${partner.id}`}
            >
              {partner.recommended && (
                <Badge className="absolute -top-2 left-4 bg-primary text-xs">
                  <Star className="h-3 w-3 mr-1 fill-current" />
                  Recommended
                </Badge>
              )}
              
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`w-3 h-3 rounded-full ${partner.color}`} />
                    <h4 className="font-semibold">{partner.name}</h4>
                  </div>
                  <p className="text-sm text-primary/80 mb-2">{partner.description}</p>
                  <p className="text-xs text-muted-foreground mb-3">{partner.tagline}</p>
                  
                  <div className="flex flex-wrap gap-1.5">
                    {partner.features.map((feature) => (
                      <Badge 
                        key={feature} 
                        variant="secondary" 
                        className="text-xs font-normal"
                      >
                        <CheckCircle className="h-3 w-3 mr-1" />
                        {feature}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="shrink-0 rounded-lg"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePartnerClick(partner);
                  }}
                >
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Learn More
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Insurance Badge Info */}
        <div className="p-3 rounded-lg bg-muted/50 border border-border/50">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
            <div className="text-xs text-muted-foreground">
              <p className="font-medium text-foreground mb-1">
                Get the "Insured" Badge
              </p>
              <p>
                Once you purchase coverage, update your profile to display the 
                "Insured" badge. This builds trust with Bookers and can help you 
                get more bookings.
              </p>
            </div>
          </div>
        </div>

        {/* Expandable Disclaimer */}
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {showDetails ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
            View Important Disclaimer
          </button>
          
          {showDetails && (
            <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 text-xs text-muted-foreground space-y-2">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-amber-600 mb-1">
                    Important Disclaimer
                  </p>
                  <ul className="space-y-1 list-disc pl-4">
                    <li>
                      <strong>No Affiliation:</strong> Bookd is not affiliated with, 
                      endorsed by, or partnered with any insurance provider listed above.
                    </li>
                    <li>
                      <strong>Recommendation Only:</strong> These links are provided as 
                      suggestions. You are free to choose any insurance provider.
                    </li>
                    <li>
                      <strong>No Liability:</strong> Bookd assumes no responsibility for 
                      insurance coverage, claims, disputes, or policy terms.
                    </li>
                    <li>
                      <strong>Review Terms:</strong> Please carefully review each provider's 
                      terms, conditions, and coverage before purchasing.
                    </li>
                    <li>
                      <strong>Your Decision:</strong> The decision to purchase insurance is 
                      entirely optional and at your sole discretion.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Decline Checkbox (for checkout) */}
        {showDeclineCheckbox && (
          <>
            <Separator />
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="decline-insurance" 
                checked={hasDeclined}
                onCheckedChange={handleDecline}
                data-testid="decline-insurance-checkbox"
              />
              <Label 
                htmlFor="decline-insurance" 
                className="text-sm text-muted-foreground cursor-pointer"
              >
                I understand the benefits of insurance and choose to proceed without coverage at this time
              </Label>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default InsuranceRecommendation;
